import { Alert, AlertTitle, Grid, Typography } from '@mui/material';
import { AddBox, Apartment, BookmarkAdd, IndeterminateCheckBox } from '@mui/icons-material';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useTranslation } from 'react-i18next';
import { RecruitText } from 'src/shared/models/enums/recruitText.enum';
import { MemberType } from 'src/shared/models/invoiceMission/invoiceMission';
import { useFormContext } from 'react-hook-form';
import { ListDisplay } from './listDisplay';

export const SelectionWithProfilesSection = () => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();

  const associations = getValues('associations') || [];
  const profilesMarkItems = getValues('profilesMarkItems') || [];
  const profilesIncludeItems = getValues('profilesIncludeItems') || [];
  const profilesExcludeItems = getValues('profilesExcludeItems') || [];

  const getRecruitedText = (key: RecruitText) => {
    const recruitTexts: Record<RecruitText, string> = {
      [RecruitText.Recruit0]: 'invoice:adminInvoiceTemplatePage.recruit.recruit0',
      [RecruitText.Recruit1]: 'invoice:adminInvoiceTemplatePage.recruit.recruit1',
      [RecruitText.Recruit2]: 'invoice:adminInvoiceTemplatePage.recruit.recruit2',
    };

    return t(recruitTexts[key] || 'invoice:adminInvoiceTemplatePage.recruit.unknown');
  };

  const getMemberTypeText = (key: MemberType) => {
    switch (key) {
      case MemberType.Active:
        return t('invoice:adminInvoiceTemplatePage.memberType.active');
      case MemberType.All:
        return t('invoice:adminInvoiceTemplatePage.memberType.all');
      case MemberType.Interested:
        return t('invoice:adminInvoiceTemplatePage.memberType.interested');
      case MemberType.Other:
        return t('invoice:adminInvoiceTemplatePage.memberType.other');
      case MemberType.Resigned:
        return t('invoice:adminInvoiceTemplatePage.memberType.resigned');
      case MemberType.Unknown:
        return t('invoice:adminInvoiceTemplatePage.memberType.unknown');
      default:
        return '';
    }
  };

  return (
    <Grid container>
      <Grid container spacing={3} justifyContent='space-between'>
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            disabled
            name='memberType'
            label={t('invoice:adminInvoiceTemplatePage.templateInformation.memberType')}
            value={getMemberTypeText(getValues('memberType'))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledDatePicker disabled name='expireDateFrom' label={t('invoice:adminInvoiceTemplatePage.expireDateFrom')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledDatePicker disabled name='expireDateTom' label={t('invoice:adminInvoiceTemplatePage.expireDateTom')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            disabled
            name='resDate'
            label={t('invoice:adminInvoiceTemplatePage.templateInformation.exitDate')}
            value={getMemberTypeText(getValues('resdate'))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField disabled name='selectionName' label={t('invoice:adminInvoiceTemplatePage.templateInformation.savedSelection')} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <ControlledTextField disabled name='newsPaperText' label={t('invoice:adminInvoiceTemplatePage.templateInformation.newspaper')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            disabled
            name='recruit'
            label={t('invoice:adminInvoiceTemplatePage.recruit.recruited')}
            value={getRecruitedText(getValues('recruit'))}
          />
        </Grid>
      </Grid>

      <Grid container mt={2} mb={1}>
        <Grid item xs={12}>
          <Alert icon={false} severity='info' sx={{ mb: 2 }} elevation={1}>
            {associations && associations.length >= 0 && (
              <Grid item xs={12}>
                <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                  <Apartment fontSize='small' sx={{ mr: 1 }} aria-hidden />
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.notifyAssociations')}&nbsp;<strong>({associations.length})</strong>
                </AlertTitle>
                {associations.length > 0 ? (
                  <ListDisplay items={associations} />
                ) : (
                  <ListDisplay items={[{ id: 0, name: t('invoice:adminInvoiceTemplatePage.templateInformation.allAssociations') }]} />
                )}
              </Grid>
            )}

            {profilesIncludeItems && profilesIncludeItems.length >= 0 && (
              <Grid item xs={12} mt={3}>
                <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                  <AddBox fontSize='small' sx={{ mr: 1 }} aria-hidden />
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.includedProfiles')}&nbsp;
                  <strong>({profilesIncludeItems.length})</strong>
                </AlertTitle>
                {profilesIncludeItems.length > 0 ? (
                  <ListDisplay items={profilesIncludeItems} />
                ) : (
                  <ListDisplay items={[{ id: 0, name: t('invoice:adminInvoiceTemplatePage.templateInformation.noneSelected') }]} />
                )}
              </Grid>
            )}
            {profilesExcludeItems && profilesExcludeItems.length >= 0 && (
              <Grid item xs={12} mt={3}>
                <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                  <IndeterminateCheckBox fontSize='small' sx={{ mr: 1 }} aria-hidden />
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.excludedProfiles')}&nbsp;
                  <strong>({profilesExcludeItems.length})</strong>
                </AlertTitle>
                {profilesExcludeItems.length > 0 ? (
                  <ListDisplay items={profilesExcludeItems} />
                ) : (
                  <ListDisplay items={[{ id: 0, name: t('invoice:adminInvoiceTemplatePage.templateInformation.noneSelected') }]} />
                )}
              </Grid>
            )}

            <Grid container mt={2} spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.tagging')}
                </Typography>
              </Grid>
              {profilesMarkItems && profilesMarkItems.length >= 0 && (
                <Grid item xs={12}>
                  <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <BookmarkAdd fontSize='small' sx={{ mr: 1 }} aria-hidden />
                    {t('invoice:adminInvoiceTemplatePage.templateInformation.taggedWithProfiles')}&nbsp;
                    <strong>({profilesMarkItems.length})</strong>
                  </AlertTitle>
                  {profilesMarkItems.length > 0 ? (
                    <ListDisplay items={profilesMarkItems} />
                  ) : (
                    <ListDisplay items={[{ id: 0, name: t('invoice:adminInvoiceTemplatePage.templateInformation.noneSelected') }]} />
                  )}
                </Grid>
              )}
            </Grid>
          </Alert>
        </Grid>
      </Grid>
    </Grid>
  );
};
